<script setup>
import {
  ref, watch, nextTick,
  useTemplateRef,
  onMounted,
  computed,
} from 'vue';
import { uniqueId } from 'lodash';
import { getColoresTipoNota } from '@/libs/chart';
import { showApiErrors } from '@/libs/cifra/flash';
import apiItemsDiarios from '@/api/cifra/items_diarios';
import NoHayDatos from '@/components/cifra/charts/NoHayDatos.vue';
import Overlay from '@/libs/cifra/overlay';

const props = defineProps({
  alumno: {
    type: Object,
    default: null,
  },
  idAsignatura: {
    type: Number,
    default: null,
  },
  idEvaluacion: {
    type: Number,
    default: null,
  },
  fecha: {
    type: String,
    default: null,
  },
  vistaFamilia: {
    type: Boolean,
    default: false,
  },
});

const contenedor = useTemplateRef('contenedor');

const estadisticas = ref(null);
const evaluaciones = ref(null);
const evaluacionActual = ref(null);
// ID único para los elementos del DOM porque en tutorías se muestran por un
// lado los charts y luego si hacen click en estadísticas, se muestra un modal
// también con los charts y no pueden compartir el mismo id en el DOM
const idUnico = uniqueId();

const classWrapperGrafico = computed(() => (
  (estadisticas.value || []).length > 1 ? 'col-md-6' : 'col-md-12'
));

/* global Chart, ChartDataLabels */
function crearChart(estadistica) {
  const colores = getColoresTipoNota(estadistica.definicion.tipo_nota);
  const chart = new Chart(document.getElementById(`${idUnico}-chart-${estadistica.definicion.id}`), {
    plugins: [ChartDataLabels],
    type: 'doughnut',
    data: {
      labels: estadistica.valores.map((valor) => valor.opcion.nombre),
      datasets: [
        {
          data: estadistica.valores.map((valor) => valor.numero),
          backgroundColor: colores.background,
          borderColor: colores.border,
          borderWidth: 1,
        },
      ],
    },
    options: {
      responsive: false,
      maintainAspectRatio: true,
      aspectRatio: 1,
      legend: {
        display: true,
        position: 'bottom',
      },
      tooltips: {
        callbacks: {
          label(tooltipItem, data) {
            const label = data.labels[tooltipItem.index];
            const valor = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
            return `${label}: ${valor}`;
          },
        },
      },
      plugins: {
        datalabels: {
          formatter(value, _context) {
            // Calcular el porcentaje en vez de el valor absoluto
            const total = _context.dataset.data.reduce((acc, curr) => acc + curr, 0);
            const porcentaje = total > 0 ? Math.round((value / total) * 100) : 0;
            if (porcentaje === 0) {
              return '';
            }
            return `${porcentaje}%`;
          },
        },
      },
    },
  });
  return chart;
}

async function cargarEstadisticas(idEvaluacion) {
  if (!props.alumno) return;

  const overlay = new Overlay(contenedor.value);
  overlay.show();
  try {
    const response = await apiItemsDiarios.estadisticasAlumno(
      props.alumno.id,
      props.idAsignatura,
      idEvaluacion,
      props.fecha,
      props.vistaFamilia,
    );

    estadisticas.value = response.data.estadisticas;
    evaluaciones.value = response.data.evaluaciones;
    evaluacionActual.value = response.data.evaluacion;

    nextTick(() => {
      // Renderizar gráficos después de que el DOM se haya actualizado
      estadisticas.value.forEach((estadistica, index) => {
        crearChart(estadistica, index);
      });
    });
  } catch (error) {
    showApiErrors(error);
  } finally {
    overlay.hide();
  }
}

// Cargar estadísticas cuando cambie la evaluación actual
watch(evaluacionActual, (nuevaEvaluacion, oldEvaluacion) => {
  // Si no había evaluación es porque es la primera carga
  if (!oldEvaluacion || nuevaEvaluacion?.id === oldEvaluacion?.id) {
    return;
  }
  if (nuevaEvaluacion) {
    cargarEstadisticas(nuevaEvaluacion.id);
  }
});

onMounted(() => {
  cargarEstadisticas(props.idEvaluacion);
});
</script>

<template>
  <div v-if="alumno" ref="contenedor" style="min-height: 100px;">
    <div class="m-t text-center">
      <ul class="nav nav-pills d-flex justify-content-center">
        <li
          v-for="evaluacion in evaluaciones"
          :key="evaluacion"
          class="nav-item"
          :class="{
            active: evaluacion.id === evaluacionActual?.id,
          }"
        >
          <a
            href="#"
            @click.prevent="evaluacionActual = evaluacion"
          >
            {{ evaluacion.nombre }}
          </a>
        </li>
      </ul>
    </div>
    <div class="contenedor-graficos">
      <div class="row">
        <template v-for="(estadistica, index) in estadisticas" :key="index">
          <div :class="classWrapperGrafico" class="m-t-md">
            <div class="grafico-estadistica">
              <h4 class="text-center">
                <img :src="estadistica.definicion.foto_url" class="imagen-definicion" />
                {{ estadistica.definicion.nombre }}
              </h4>
              <canvas
                :id="`${idUnico}-chart-${estadistica.definicion.id}`"
                class="chart-items-diarios"
              ></canvas>
            </div>
          </div>
        </template>
        <template v-if="estadisticas && estadisticas.length === 0">
          <NoHayDatos tamano="xl" class="animated fadeIn" />
        </template>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/components/cuadernos/constantes_cuaderno.scss';

.chart-items-diarios {
  max-width: 300px;
  max-height: 300px;
  margin: auto;
}
.imagen-definicion {
  width: 64px;
  height: 64px;
  margin-right: $margin-xs;
}
</style>
